import React, { useState, useEffect, useRef } from 'react'
import { FaWhatsapp, FaTimes } from 'react-icons/fa'
import { useUserContext } from '../../context/users/UserContext'
import './WhatsAppButton.css'

// Visibilidad global: en App.js solo se monta si REACT_APP_SHOW_WHATSAPP_BUTTON=true

// Mensajes amigables cortos que aparecerán cada cierto tiempo
const friendlyMessages = [
    '¿Todo bien? Si ocupás ayuda, escribinos 😊',
    '¿Alguna duda? Estamos aquí para ayudarte',
    '¿Necesitás ayuda? Escríbenos 💚',
    'Si tenés algún inconveniente, contactanos',
    '¿Todo en orden? Estamos para ayudarte',
    '¿Necesitas algo? Acá estamos 👍',
  ]

const WhatsAppButton = () => {
  const { authenticatedUser, isAuthenticated } = useUserContext()
  const [showModal, setShowModal] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipMessage, setTooltipMessage] = useState('')
  const modalRef = useRef(null)
  const tooltipTimerRef = useRef(null)
  const messageIndexRef = useRef(0)

  // Número de WhatsApp (Costa Rica usa +506)
  const phoneNumber = '50670504809' // Formato internacional sin + ni espacios

  // Mostrar mensajes amigables cada 30 segundos
  useEffect(() => {
    const showFriendlyMessage = () => {
      const randomMessage = friendlyMessages[messageIndexRef.current % friendlyMessages.length]
      setTooltipMessage(randomMessage)
      setShowTooltip(true)

      // Ocultar después de 5 segundos
      tooltipTimerRef.current = setTimeout(() => {
        setShowTooltip(false)
      }, 5000)

      messageIndexRef.current++
    }

    // Mostrar el primer mensaje después de 10 segundos
    const initialTimer = setTimeout(showFriendlyMessage, 10000)

    // Luego mostrar cada 30 segundos
    const intervalTimer = setInterval(showFriendlyMessage, 30000)

    return () => {
      clearTimeout(initialTimer)
      clearInterval(intervalTimer)
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current)
      }
    }
  }, [])

  // Cerrar modal al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowModal(false)
      }
    }

    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showModal])

  // Obtener información del usuario
  const getUserInfo = () => {
    if (isAuthenticated && authenticatedUser) {
      const fullName = authenticatedUser.fullName || 'Usuario'
      const role = getRoleName(authenticatedUser.permissions)
      return { fullName, role }
    }
    return { fullName: 'Visitante', role: 'Sin sesión' }
  }

  // Obtener nombre del rol en español
  const getRoleName = (permissions) => {
    if (!permissions || permissions.length === 0) return 'Usuario'
    
    const roleMap = {
      'ADMIN': 'Administrador',
      'ANFITRION_ORG': 'Anfitrión Organizacional',
      'ANFITRION_PER': 'Anfitrión Personal',
      'ANFITRION': 'Anfitrión',
      'INVITADO': 'Invitado'
    }

    const userRole = permissions[0]
    return roleMap[userRole] || userRole
  }

  // Abrir WhatsApp con mensaje predefinido
  const openWhatsApp = () => {
    const { fullName, role } = getUserInfo()
    
    // Mensaje predefinido
    const message = `Hola, soy *${fullName}* 👋
    
Rol: ${role}
    
Necesito ayuda con la plataforma Alleanza. ¿Podrían asistirme?

Gracias de antemano.`

    // Codificar el mensaje para URL
    const encodedMessage = encodeURIComponent(message)
    
    // Detectar si es móvil o desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    
    // URL de WhatsApp
    const whatsappUrl = isMobile 
      ? `whatsapp://send?phone=${phoneNumber}&text=${encodedMessage}`
      : `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`

    // Abrir en nueva ventana
    window.open(whatsappUrl, '_blank')
    
    // Cerrar modal
    setShowModal(false)
  }

  // Toggle modal
  const handleButtonClick = () => {
    setShowModal(!showModal)
    setShowTooltip(false)
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current)
    }
  }

  // Mostrar tooltip al hover
  const handleMouseEnter = () => {
    if (!showModal) {
      setTooltipMessage('¿Necesitás ayuda? ¡Hacé clic aquí!')
      setShowTooltip(true)
    }
  }

  const handleMouseLeave = () => {
    if (!showModal) {
      setShowTooltip(false)
    }
  }

  const { fullName, role } = getUserInfo()

  // No mostrar el botón si el usuario no está autenticado (incluye página de login)
  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="whatsapp-float-container" ref={modalRef}>
      {/* Tooltip */}
      <div className={`whatsapp-tooltip ${showTooltip && !showModal ? 'show' : ''}`}>
        <p className="whatsapp-tooltip-text">{tooltipMessage}</p>
      </div>

      {/* Modal de ayuda */}
      <div className={`whatsapp-modal ${showModal ? 'show' : ''}`}>
        <div className="whatsapp-modal-header">
          <button 
            className="whatsapp-modal-close" 
            onClick={() => setShowModal(false)}
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
          <h3 className="whatsapp-modal-title">
            <FaWhatsapp style={{ fontSize: '24px' }} />
            Soporte Alleanza
          </h3>
          <p className="whatsapp-modal-subtitle">
            Estamos aquí para ayudarte
          </p>
        </div>
        
        <div className="whatsapp-modal-body">
          <div className="whatsapp-modal-section">
            <span className="whatsapp-modal-label">Tu información</span>
            <div className="whatsapp-modal-info">
              <strong>{fullName}</strong>
              <br />
              <small style={{ color: '#666' }}>{role}</small>
            </div>
          </div>

          <div className="whatsapp-instructions">
            <p className="whatsapp-instructions-title">
              ¿Cómo podemos ayudarte?
            </p>
            <ul className="whatsapp-instructions-list">
              <li>Problemas técnicos</li>
              <li>Dudas sobre funcionalidades</li>
              <li>Consultas generales</li>
              <li>Sugerencias y feedback</li>
            </ul>
          </div>

          <button 
            className="whatsapp-help-button"
            onClick={openWhatsApp}
          >
            <FaWhatsapp style={{ fontSize: '20px' }} />
            Solicitar Ayuda
          </button>

          <p style={{ 
            fontSize: '11px', 
            color: '#999', 
            textAlign: 'center', 
            marginTop: '12px',
            marginBottom: 0 
          }}>
            Se abrirá WhatsApp con un mensaje prellenado
          </p>
        </div>
      </div>

      {/* Botón flotante */}
      <button
        className="whatsapp-button"
        onClick={handleButtonClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label="Abrir chat de WhatsApp"
        title="¿Necesitás ayuda?"
      >
        <FaWhatsapp className="whatsapp-icon" />
      </button>
    </div>
  )
}

export default WhatsAppButton

